import React, { useState } from 'react';
import { OrdemDeServicoFormData } from '../../types/ordemServico';
import { Truck, UserCheck, Shield, DollarSign, UserPlus, Search, X } from 'lucide-react';
import { TrocarMotoristaVeiculoModal } from './TrocarMotoristaVeiculoModal';

interface OsFormTabVeiculoProps {
  formData: OrdemDeServicoFormData;
  setFormData: React.Dispatch<React.SetStateAction<OrdemDeServicoFormData>>;
  tipoVeiculoOptions: { id: number; tipo_veiculo: string }[];
  veiculosOptions: { 
    id: string; 
    placa: string; 
    motorista_id?: number; 
    motorista_nome?: string; 
    agregado_id?: number; 
    valor_repasse_agregado?: number;
    veiculo_marca_id?: string | number;
    veiculo_modelo_id?: string | number;
    veiculo_cor_id?: string | number;
    tipo_veiculo?: string | number;
    isStandaloneDriver?: boolean;
    status_frota?: string;
    ativo?: boolean;
  }[];
  pessoasOptions: { id: number; nome: string; fone?: string }[];
  marcasOptions: { id: number; marca: string }[];
  modelosOptions: { id: number; modelo: string }[];
  coresOptions: { id: number; cor: string }[];
  onMotoristaAtualizado?: (veiculoId: string, motoristaId: number, motoristaNome: string, newPessoaObj?: any) => void;
}

export const OsFormTabVeiculo: React.FC<OsFormTabVeiculoProps> = ({
  formData,
  setFormData,
  tipoVeiculoOptions,
  veiculosOptions,
  pessoasOptions,
  marcasOptions,
  modelosOptions,
  coresOptions,
  onMotoristaAtualizado,
}) => {
  // SEÇÃO: Controle de Alocação (Frota vs Agregado)
  const [tipoAlocacao, setTipoAlocacao] = useState<'frota' | 'agregado'>('frota');
  const [isTrocarModalOpen, setIsTrocarModalOpen] = useState(false);

  // Estados de busca rápida ("buscar ao digitar")
  const [searchVeiculoTerm, setSearchVeiculoTerm] = useState('');
  const [searchMotoristaTerm, setSearchMotoristaTerm] = useState('');

  // Sincroniza estado inicial uma única vez ao carregar uma OS existente
  const initialSyncRef = React.useRef(false);
  React.useEffect(() => {
    if (!initialSyncRef.current) {
      const temRepasse = Number(formData.repasse) > 0;
      let isAgregado = temRepasse;

      if (formData.veiculos_id) {
        const v = veiculosOptions.find(opt => String(opt.id) === String(formData.veiculos_id));
        if (v?.agregado_id || (v as any)?.isStandaloneDriver) {
          isAgregado = true;
        }
      }

      if (isAgregado) {
        setTipoAlocacao('agregado');
      } else {
        setTipoAlocacao('frota');
      }
      initialSyncRef.current = true;
    }
  }, [formData.veiculos_id, formData.repasse, veiculosOptions]);

  // Handler para alternar para Frota Própria
  const handleSwitchToFrota = () => {
    setTipoAlocacao('frota');
    setFormData((prev) => {
      // Verifica se o veículo atualmente selecionado é de agregado ou motorista avulso
      const currentV = veiculosOptions.find((v) => String(v.id) === String(prev.veiculos_id));
      const isAgregadoVehicle = !!currentV?.agregado_id || !!(currentV as any)?.isStandaloneDriver;

      return {
        ...prev,
        repasse: 0, // ZERA o repasse para frota própria!
        ...(isAgregadoVehicle
          ? {
              veiculos_id: '',
              placa: '',
              veiculo_marca_id: '',
              modelo: '',
              cor: '',
              tipo_veiculo_id: '',
              agregado: '',
            }
          : {}),
      };
    });
  };

  // Handler para alternar para Agregado / Terceirizado
  const handleSwitchToAgregado = () => {
    setTipoAlocacao('agregado');
    setFormData((prev) => {
      // Verifica se o veículo atualmente selecionado é da frota própria
      const currentV = veiculosOptions.find((v) => String(v.id) === String(prev.veiculos_id));
      const isFrotaVehicle = currentV && !currentV.agregado_id && !(currentV as any)?.isStandaloneDriver;

      return {
        ...prev,
        ...(isFrotaVehicle
          ? {
              veiculos_id: '',
              placa: '',
              veiculo_marca_id: '',
              modelo: '',
              cor: '',
              tipo_veiculo_id: '',
              agregado: '',
            }
          : {}),
      };
    });
  };

  // SEÇÃO: Filtragem de Veículos (Exibe apenas frotas ou agregados, ocultando veículos em manutenção/desativados)
  const filteredVeiculos = React.useMemo(() => {
    return veiculosOptions.filter((v) => {
      const isCurrentlySelected = String(v.id) === String(formData.veiculos_id);
      if (!isCurrentlySelected && (v.status_frota === 'em_manutencao' || v.status_frota === 'desativado' || v.ativo === false)) {
        return false;
      }
      const isAgregado = !!v.agregado_id;
      if (tipoAlocacao === 'agregado') {
        return isAgregado || v.isStandaloneDriver;
      } else {
        // Frota Própria: Apenas veículos de frota cadastrados
        if (v.isStandaloneDriver) {
          return false;
        }
        return !isAgregado;
      }
    });
  }, [veiculosOptions, tipoAlocacao, formData.veiculos_id]);

  // Busca ao digitar para Veículos
  const displayVeiculos = React.useMemo(() => {
    if (!searchVeiculoTerm.trim()) return filteredVeiculos;
    const term = searchVeiculoTerm.toLowerCase().trim();
    return filteredVeiculos.filter((v) => {
      const nomeMatch = v.motorista_nome && v.motorista_nome.toLowerCase().includes(term);
      const placaMatch = v.placa && v.placa.toLowerCase().includes(term);
      const idMatch = String(v.id).includes(term);
      return nomeMatch || placaMatch || idMatch;
    });
  }, [filteredVeiculos, searchVeiculoTerm]);

  // Busca ao digitar para Motoristas / Agregados
  const displayPessoas = React.useMemo(() => {
    if (!searchMotoristaTerm.trim()) return pessoasOptions;
    const term = searchMotoristaTerm.toLowerCase().trim();
    return pessoasOptions.filter((p) => {
      const nomeMatch = p.nome && p.nome.toLowerCase().includes(term);
      const idMatch = String(p.id).includes(term);
      const foneMatch = p.fone && p.fone.includes(term);
      return nomeMatch || idMatch || foneMatch;
    });
  }, [pessoasOptions, searchMotoristaTerm]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    let finalValue: any = value;

    if (type === 'number') {
      finalValue = value === '' ? 0 : parseFloat(value);
    }

    setFormData((prev) => ({ ...prev, [name]: finalValue }));
  };

  // Resolve os nomes amigáveis de marca, modelo e cor baseado no ID
  const selectedMarcaNome = React.useMemo(() => {
    if (!formData.veiculo_marca_id) return '';
    const found = marcasOptions.find((m) => m.id.toString() === formData.veiculo_marca_id.toString());
    return found ? found.marca : `Marca #${formData.veiculo_marca_id}`;
  }, [formData.veiculo_marca_id, marcasOptions]);

  const selectedModeloNome = React.useMemo(() => {
    if (!formData.modelo) return '';
    const found = modelosOptions.find((m) => m.id.toString() === formData.modelo.toString());
    return found ? found.modelo : `Modelo #${formData.modelo}`;
  }, [formData.modelo, modelosOptions]);

  const selectedCorNome = React.useMemo(() => {
    if (!formData.cor) return '';
    const found = coresOptions.find((c) => c.id.toString() === formData.cor.toString());
    return found ? found.cor : `Cor #${formData.cor}`;
  }, [formData.cor, coresOptions]);

  // Objeto do veículo atualmente selecionado (se houver)
  const selectedVeiculoObj = React.useMemo(() => {
    if (!formData.veiculos_id) return null;
    const found = veiculosOptions.find((v) => String(v.id) === String(formData.veiculos_id));
    if (!found) return null;
    return {
      id: String(found.id),
      placa: found.placa || String(found.id),
      motorista_id: found.motorista_id,
      motorista_nome: found.motorista_nome,
    };
  }, [formData.veiculos_id, veiculosOptions]);

  // Quando escolhe o motorista/veículo no combo
  const handleSelectVeiculo = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedVeiculoId = e.target.value;
    if (!selectedVeiculoId) {
      setFormData((prev) => ({
        ...prev,
        veiculos_id: '',
        agregado: '',
        placa: '',
        veiculo_marca_id: '',
        modelo: '',
        cor: '',
        tipo_veiculo_id: '',
        repasse: 0,
      }));
      return;
    }

    const foundVeiculo: any = veiculosOptions.find((v) => String(v.id) === String(selectedVeiculoId));

    if (foundVeiculo) {
      if (foundVeiculo.isStandaloneDriver) {
        const isAgregado = !!foundVeiculo.agregado_id;
        setFormData((prev) => ({
          ...prev,
          veiculos_id: '',
          placa: prev.placa || '',
          agregado: foundVeiculo.agregado_id || foundVeiculo.motorista_id || prev.agregado,
          repasse: isAgregado ? Number(foundVeiculo.valor_repasse_agregado || prev.repasse || 0) : 0,
        }));
      } else {
        const isAgregado = !!foundVeiculo.agregado_id;
        setFormData((prev) => ({
          ...prev,
          veiculos_id: selectedVeiculoId,
          placa: foundVeiculo.placa || prev.placa,
          veiculo_marca_id: foundVeiculo.veiculo_marca_id ? String(foundVeiculo.veiculo_marca_id) : prev.veiculo_marca_id,
          modelo: foundVeiculo.veiculo_modelo_id ? String(foundVeiculo.veiculo_modelo_id) : prev.modelo,
          cor: foundVeiculo.veiculo_cor_id ? String(foundVeiculo.veiculo_cor_id) : prev.cor,
          tipo_veiculo_id: foundVeiculo.tipo_veiculo ? String(foundVeiculo.tipo_veiculo) : prev.tipo_veiculo_id,
          agregado: foundVeiculo.motorista_id || foundVeiculo.agregado_id || prev.agregado,
          // Para frota própria, o repasse DEVE ser ZERO! Para agregado, usa o repasse cadastrado
          repasse: isAgregado ? Number(foundVeiculo.valor_repasse_agregado || prev.repasse || 0) : 0,
        }));
      }
    }
  };

  // Quando escolhe o motorista/agregado na lista de pessoas
  const handleSelectPessoa = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const pessoaId = e.target.value;
    if (!pessoaId) {
      setFormData((prev) => ({ ...prev, agregado: '' }));
      return;
    }

    if (tipoAlocacao === 'agregado') {
      // Procura se o agregado selecionado possui um veículo vinculado
      const veiculoDoAgregado = veiculosOptions.find((v) => String(v.agregado_id) === String(pessoaId));
      setFormData((prev) => ({
        ...prev,
        agregado: pessoaId,
        ...(veiculoDoAgregado
          ? {
              veiculos_id: String(veiculoDoAgregado.id),
              placa: veiculoDoAgregado.placa || prev.placa,
              veiculo_marca_id: veiculoDoAgregado.veiculo_marca_id ? String(veiculoDoAgregado.veiculo_marca_id) : prev.veiculo_marca_id,
              modelo: veiculoDoAgregado.veiculo_modelo_id ? String(veiculoDoAgregado.veiculo_modelo_id) : prev.modelo,
              cor: veiculoDoAgregado.veiculo_cor_id ? String(veiculoDoAgregado.veiculo_cor_id) : prev.cor,
              tipo_veiculo_id: veiculoDoAgregado.tipo_veiculo ? String(veiculoDoAgregado.tipo_veiculo) : prev.tipo_veiculo_id,
              repasse: Number(veiculoDoAgregado.valor_repasse_agregado || prev.repasse || 0),
            }
          : {}),
      }));
    } else {
      // Frota Própria: Altera o motorista da viagem e garante repasse ZERO
      setFormData((prev) => ({
        ...prev,
        agregado: pessoaId,
        repasse: 0,
      }));
    }
  };

  return (
    <div className="space-y-4 p-4 bg-slate-50 border border-slate-200/80 rounded-xl">
      <div className="flex items-center gap-2 border-b border-slate-200 pb-2">
        <Truck className="w-4 h-4 text-red-600" />
        <h4 className="text-xs font-bold uppercase tracking-wider text-slate-700">
          Informações do Veículo & Motorista Alocado
        </h4>
      </div>

      {/* SEÇÃO: Seletor de Tipo de Alocação (SEMPRE VISÍVEIS AMBOS OS BOTÕES) */}
      <div className="bg-white p-3 rounded-xl border border-slate-200 shadow-2xs space-y-2">
        <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider flex items-center justify-between">
          <span>Tipo de Alocação do Transporte</span>
          <span className="text-3xs text-slate-400 font-normal lowercase">
            (Clique para alternar entre Frota Própria e Agregado)
          </span>
        </label>
        <div className="grid grid-cols-2 gap-2">
          <button
            type="button"
            onClick={handleSwitchToFrota}
            className={`py-2 px-3 text-xs font-bold rounded-lg border transition-all text-center uppercase tracking-wider flex items-center justify-center gap-1.5 ${
              tipoAlocacao === 'frota'
                ? 'bg-red-600 text-white border-red-600 shadow-sm'
                : 'bg-slate-50 text-slate-600 border-slate-200 hover:bg-slate-100'
            }`}
          >
            <Truck className="w-3.5 h-3.5" />
            <span>Frota Própria</span>
          </button>
          <button
            type="button"
            onClick={handleSwitchToAgregado}
            className={`py-2 px-3 text-xs font-bold rounded-lg border transition-all text-center uppercase tracking-wider flex items-center justify-center gap-1.5 ${
              tipoAlocacao === 'agregado'
                ? 'bg-red-600 text-white border-red-600 shadow-sm'
                : 'bg-slate-50 text-slate-600 border-slate-200 hover:bg-slate-100'
            }`}
          >
            <Shield className="w-3.5 h-3.5" />
            <span>Agregado / Terceirizado</span>
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-12 gap-4">
        {/* Motorista / Veículo (Filtrado) */}
        <div className="sm:col-span-12">
          <div className="flex items-center justify-between gap-2 mb-1">
            <label className="text-xs font-bold text-slate-700 uppercase flex items-center gap-1">
              <UserCheck className="w-3.5 h-3.5 text-slate-500" />
              {tipoAlocacao === 'frota' ? 'Veículo da Frota Própria' : 'Veículo / Agregado Disponível'} ({displayVeiculos.length} de {filteredVeiculos.length})
            </label>

            {tipoAlocacao === 'frota' && (
              <button
                type="button"
                onClick={() => setIsTrocarModalOpen(true)}
                className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-amber-500/10 hover:bg-amber-500/20 text-amber-800 border border-amber-300/80 rounded-lg text-2xs font-bold transition-all cursor-pointer shadow-2xs"
                title="Atalho para vincular ou trocar o motorista padrão deste veículo"
              >
                <UserCheck className="w-3.5 h-3.5 text-amber-600" />
                <span>Trocar Motorista Padrão do Veículo</span>
              </button>
            )}
          </div>

          {/* Campo de Busca Rápida "Buscar ao digitar" */}
          <div className="flex items-center gap-1.5 mb-2">
            <div className="relative flex-1">
              <div className="absolute inset-y-0 left-0 pl-2.5 flex items-center pointer-events-none">
                <Search className="w-3.5 h-3.5 text-slate-400" />
              </div>
              <input
                type="text"
                value={searchVeiculoTerm}
                onChange={(e) => setSearchVeiculoTerm(e.target.value)}
                placeholder={tipoAlocacao === 'frota' ? "Buscar por placa ou motorista da frota ao digitar..." : "Buscar por placa ou nome do agregado ao digitar..."}
                className="w-full pl-8 pr-8 py-1.5 text-xs border border-slate-300 rounded-lg outline-none focus:ring-2 focus:ring-red-500 bg-white"
              />
              {searchVeiculoTerm && (
                <button
                  type="button"
                  onClick={() => setSearchVeiculoTerm('')}
                  className="absolute inset-y-0 right-0 pr-2 flex items-center text-slate-400 hover:text-slate-600"
                  title="Limpar busca"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          </div>

          <select
            name="veiculos_id"
            value={formData.veiculos_id || (formData.agregado && tipoAlocacao === 'agregado' ? `p_${formData.agregado}` : '')}
            onChange={handleSelectVeiculo}
            className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm text-slate-800 focus:ring-2 focus:ring-red-500 outline-none bg-white font-medium"
          >
            <option value="">
              {tipoAlocacao === 'frota' ? '-- Selecione um veículo da frota própria --' : '-- Selecione um veículo de agregado ou motorista --'}
            </option>
            {displayVeiculos.map((v) => (
              <option key={v.id} value={v.id}>
                {v.motorista_nome ? `${v.motorista_nome} - Placa: ${v.placa || v.id}` : `Veículo (${v.placa || v.id}) - Placa: ${v.placa || v.id}`}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-12 gap-4">
        {/* Placa */}
        <div className="sm:col-span-3">
          <label className="block text-xs font-bold text-slate-700 uppercase mb-1">
            Placa
          </label>
          <input
            type="text"
            name="placa"
            value={formData.placa}
            onChange={handleChange}
            placeholder="ABC-1234"
            className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm font-mono font-bold text-slate-800 uppercase focus:ring-2 focus:ring-red-500 outline-none"
          />
        </div>

        {/* Marca - Apenas Leitura baseado no Veículo selecionado */}
        <div className="sm:col-span-3">
          <label className="block text-xs font-bold text-slate-700 uppercase mb-1">
            Marca
          </label>
          <input
            type="text"
            value={selectedMarcaNome}
            readOnly
            disabled
            placeholder="Nenhuma"
            className="w-full border border-slate-200 bg-slate-100 rounded-lg px-3 py-2 text-sm text-slate-500 font-medium cursor-not-allowed outline-none"
          />
        </div>

        {/* Modelo - Apenas Leitura baseado no Veículo selecionado */}
        <div className="sm:col-span-3">
          <label className="block text-xs font-bold text-slate-700 uppercase mb-1">
            Modelo
          </label>
          <input
            type="text"
            value={selectedModeloNome}
            readOnly
            disabled
            placeholder="Nenhum"
            className="w-full border border-slate-200 bg-slate-100 rounded-lg px-3 py-2 text-sm text-slate-500 font-medium cursor-not-allowed outline-none"
          />
        </div>

        {/* Cor - Apenas Leitura baseado no Veículo selecionado */}
        <div className="sm:col-span-3">
          <label className="block text-xs font-bold text-slate-700 uppercase mb-1">
            Cor
          </label>
          <input
            type="text"
            value={selectedCorNome}
            readOnly
            disabled
            placeholder="Nenhuma"
            className="w-full border border-slate-200 bg-slate-100 rounded-lg px-3 py-2 text-sm text-slate-500 font-medium cursor-not-allowed outline-none"
          />
        </div>
      </div>

      {/* SEÇÃO: Exibe o Motorista da Viagem (Frota) ou Agregado e Repasse */}
      <div className="grid grid-cols-1 sm:grid-cols-12 gap-4 pt-2 border-t border-slate-200">
        {tipoAlocacao === 'agregado' ? (
          <>
            {/* Agregado */}
            <div className="sm:col-span-8">
              <label className="block text-xs font-bold text-slate-700 uppercase mb-1 flex items-center gap-1">
                <Shield className="w-3.5 h-3.5 text-blue-600" />
                Agregado / Terceirizado Responsável ({displayPessoas.length} de {pessoasOptions.length})
              </label>

              {/* Busca ao digitar */}
              <div className="flex items-center gap-1.5 mb-2">
                <div className="relative flex-1">
                  <div className="absolute inset-y-0 left-0 pl-2.5 flex items-center pointer-events-none">
                    <Search className="w-3.5 h-3.5 text-slate-400" />
                  </div>
                  <input
                    type="text"
                    value={searchMotoristaTerm}
                    onChange={(e) => setSearchMotoristaTerm(e.target.value)}
                    placeholder="Filtrar agregado ao digitar..."
                    className="w-full pl-8 pr-8 py-1.5 text-xs border border-slate-300 rounded-lg outline-none focus:ring-2 focus:ring-red-500 bg-white"
                  />
                  {searchMotoristaTerm && (
                    <button
                      type="button"
                      onClick={() => setSearchMotoristaTerm('')}
                      className="absolute inset-y-0 right-0 pr-2 flex items-center text-slate-400 hover:text-slate-600"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              </div>

              <select
                name="agregado"
                value={formData.agregado}
                onChange={handleSelectPessoa}
                className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm text-slate-800 focus:ring-2 focus:ring-red-500 outline-none bg-white font-medium"
              >
                <option value="">Selecione o agregado responsável...</option>
                {displayPessoas.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.nome}
                  </option>
                ))}
              </select>
            </div>

            {/* Valor Repasse Agregado */}
            <div className="sm:col-span-4">
              <label className="block text-xs font-bold text-slate-700 uppercase mb-1 flex items-center gap-1">
                <DollarSign className="w-3.5 h-3.5 text-blue-600" />
                Valor Repasse Agregado (R$)
              </label>
              <input
                type="number"
                step="0.01"
                name="repasse"
                value={formData.repasse}
                onChange={handleChange}
                placeholder="0.00"
                className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm font-bold text-blue-700 focus:ring-2 focus:ring-red-500 outline-none bg-white"
              />
              <p className="text-3xs text-slate-500 mt-1">
                Valor pago ao terceiro/agregado pelo frete.
              </p>
            </div>
          </>
        ) : (
          <div className="sm:col-span-12">
            <label className="block text-xs font-bold text-slate-700 uppercase mb-1 flex items-center justify-between">
              <span className="flex items-center gap-1">
                <UserCheck className="w-3.5 h-3.5 text-emerald-600" />
                Motorista Efetivo Desta Viagem ({displayPessoas.length} de {pessoasOptions.length})
              </span>
              <span className="text-3xs text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded font-semibold">
                Frota Própria (Sem Repasse)
              </span>
            </label>

            {/* Busca ao digitar */}
            <div className="flex items-center gap-1.5 mb-2">
              <div className="relative flex-1">
                <div className="absolute inset-y-0 left-0 pl-2.5 flex items-center pointer-events-none">
                  <Search className="w-3.5 h-3.5 text-slate-400" />
                </div>
                <input
                  type="text"
                  value={searchMotoristaTerm}
                  onChange={(e) => setSearchMotoristaTerm(e.target.value)}
                  placeholder="Filtrar motorista da frota ao digitar..."
                  className="w-full pl-8 pr-8 py-1.5 text-xs border border-slate-300 rounded-lg outline-none focus:ring-2 focus:ring-red-500 bg-white"
                />
                {searchMotoristaTerm && (
                  <button
                    type="button"
                    onClick={() => setSearchMotoristaTerm('')}
                    className="absolute inset-y-0 right-0 pr-2 flex items-center text-slate-400 hover:text-slate-600"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            </div>

            <select
              name="agregado"
              value={formData.agregado}
              onChange={handleSelectPessoa}
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm text-slate-800 focus:ring-2 focus:ring-red-500 outline-none bg-white font-medium"
            >
              <option value="">-- Selecione o motorista da frota responsável por esta viagem --</option>
              {displayPessoas.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.nome}
                </option>
              ))}
            </select>
            <p className="text-3xs text-slate-500 mt-1">
              💡 <em>Frota Própria:</em> O repasse é mantido em R$ 0,00 e o motorista selecionado fica registrado como condutor desta OS.
            </p>
          </div>
        )}
      </div>

      {/* Modal de Atalho para Cadastrar / Vincular / Trocar Motorista do Veículo */}
      <TrocarMotoristaVeiculoModal
        isOpen={isTrocarModalOpen}
        onClose={() => setIsTrocarModalOpen(false)}
        veiculo={selectedVeiculoObj}
        veiculosOptions={filteredVeiculos}
        pessoasOptions={pessoasOptions}
        onMotoristaAtualizado={(veiculoId, motoristaId, motoristaNome, newPessoaObj) => {
          if (onMotoristaAtualizado) {
            onMotoristaAtualizado(veiculoId, motoristaId, motoristaNome, newPessoaObj);
          }
        }}
      />
    </div>
  );
};

